/**
 * TerminationTaskSection - the Aufgaben peek of a task the intake opened for a customer's Kündigung
 * (process mandate_termination, Stefan P6 V1.0 steps 1-4, UC1/UC3/UC6).
 *
 *   «Mögliche Kündigung prüfen»   the Kündigung read from the mail (Mandat, Art, Enddatum or «abgeleitet»,
 *                                 Grund, unterzeichnet von) with «Mandat auflösen» (the dialog «Mandat
 *                                 auflösen», prefilled from it) and «Verwerfen» (no Kündigung, the task done)
 *   «Kündigung prüfen»            the Befunde of the Berechtigungsprüfung in words with «Berechtigung bestätigt»
 *                                 (a reason) and «Zurückweisen» (a Begründung)
 *   the hints                     «Enddatum anpassen?», «Kündigung erneut eingegangen», «Dokument nach
 *                                 Inaktivierung», «Abbruch statt Auflösung», «Kündigung zuordnen»: what to do,
 *                                 the Kündigung read from the mail where there is one
 *
 * The task is told by its title (the titles the api writes, TERMINATION_INTAKE_TASK). Every decision is the
 * api's; the actions need mandate.eingangskontrolle (the routes' key).
 *
 * TerminationFreigabeSection: the Freigabe task «Mandatsauflösung freigeben» (the header chip's target) with
 * «Mandat auflösen», the dialog resumed on the live termination; the button needs mandate.dispatch (the
 * freigeben route's key).
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TERMINATION_CHECK_CODE_LABEL,
  TERMINATION_FREIGABE_TASK,
  TERMINATION_INTAKE_TASK,
  TERMINATION_OVERRIDE_REASON_CODES,
  TERMINATION_REMARK_MAX,
  formatIsoDayDe,
} from '@brokly/shared';
import type { TerminationOverrideReasonCode, TerminationProposalDto, TerminationIntakeTaskKind } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { ApiError, apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import { refreshMandateQueries } from '../../entity-detail/mandate-state-queries';
import { MandatAufloesenDialog, terminationErrorText } from './MandatAufloesenDialog';

type SubjectType = 'person' | 'firma';

/** The key of every action of the section (the routes' key). */
export const TERMINATION_INTAKE_KEY = 'mandate.eingangskontrolle';
/** The Freigabe key (the freigeben route's key). */
export const TERMINATION_FREIGABE_KEY = 'mandate.dispatch';

/** The Freigabe task «Mandatsauflösung freigeben» of an eröffnete termination, told by its title. */
export function isTerminationFreigabeTask(title: string | null | undefined): boolean {
  return title === TERMINATION_FREIGABE_TASK.title;
}

const KIND_BY_TITLE: Record<string, TerminationIntakeTaskKind> = Object.fromEntries(
  Object.values(TERMINATION_INTAKE_TASK).map((t) => [t.title, t.kind]),
);

/** The intake task kind of a mandate_termination task by its title, or null (the S1 Freigabe task, a manual one). */
export function terminationTaskKind(title: string | null | undefined): TerminationIntakeTaskKind | null {
  return title ? (KIND_BY_TITLE[title] ?? null) : null;
}

/** The section heading per kind. */
export function terminationSectionTitle(kind: TerminationIntakeTaskKind): string {
  return kind === 'termination_check' ? 'Berechtigungsprüfung' : kind === 'termination_zuordnen' ? 'Zuordnen' : 'Kündigung';
}

export const TERMINATION_OVERRIDE_REASON_LABEL: Record<TerminationOverrideReasonCode, string> = {
  unterschrift_geprueft: 'Unterschrift geprüft',
  vollmacht_vorhanden: 'Vollmacht liegt vor',
  telefonisch_bestaetigt: 'Telefonisch bestätigt',
  sonstiges: 'Sonstiges',
};

const HINT_TEXT: Partial<Record<TerminationIntakeTaskKind, string>> = {
  termination_enddatum:
    'Die Kündigung nennt ein früheres Enddatum als die laufende Mandatsauflösung. Für ein neues Enddatum die Kündigung widerrufen und neu auflösen, sonst die Aufgabe erledigen.',
  termination_erneut: 'Die Kündigung ist erneut eingegangen; die laufende Mandatsauflösung bleibt unverändert.',
  termination_inaktiv: 'Der Kunde ist bereits inaktiv. Das Dokument ist beim Kunden abgelegt, der Status bleibt unverändert.',
  termination_abbruch: 'Ein Mandatsvorgang des Kunden ist noch offen. Den Mandatsvorgang abbrechen statt das Mandat aufzulösen.',
  termination_zuordnen:
    'Die Kündigung lässt sich keinem Kunden eindeutig zuordnen (mehrere Kunden kommen in Frage). Den Kunden prüfen und die Mandatsauflösung beim Kunden eröffnen.',
};

/** An unverified sender's Kündigung was not filed (it stays on the mail); the hints say so. */
const HINT_TEXT_UNVERIFIED: Partial<Record<TerminationIntakeTaskKind, string>> = {
  termination_inaktiv:
    'Der Kunde ist bereits inaktiv. Der Absender ist nicht verifiziert: das Dokument ist nicht beim Kunden abgelegt, es liegt bei der E-Mail.',
};

const KIND_LABEL: Record<TerminationProposalDto['mandateKind'], string> = { broker: 'Brokermandat', analyse: 'Analysemandat', unklar: 'unklar' };
const ART_LABEL: Record<TerminationProposalDto['art'], string> = { sofort: 'sofort', stichtag: 'per Stichtag', unklar: 'unklar' };
const DECISION_LABEL: Record<NonNullable<TerminationProposalDto['decision']>, string> = {
  uebernommen: 'Übernommen: die Mandatsauflösung ist eröffnet.',
  korrigiert: 'Korrigiert übernommen: die Mandatsauflösung ist eröffnet.',
  verworfen: 'Verworfen: keine Kündigung.',
};

function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-right text-sm text-foreground">{children}</span>
    </div>
  );
}

/** The Kündigung read from the mail (no mail text; the signer names are the customer's own data). */
function ProposalFacts({ p }: { p: TerminationProposalDto }) {
  const endDate = p.art === 'sofort' ? null : p.endDate;
  return (
    <div className="flex flex-col" data-testid="termination-proposal">
      {(p.reviewReasons ?? []).includes('absender_nicht_verifiziert') && (
        <p className="py-1 text-sm text-status-warning" data-testid="termination-sender-unverified">
          {TERMINATION_CHECK_CODE_LABEL.absender_nicht_verifiziert}
        </p>
      )}
      <Row label="Eingang">{p.channel === 'upload' ? 'Upload' : 'E-Mail'}</Row>
      <Row label="Mandat">{KIND_LABEL[p.mandateKind]}</Row>
      <Row label="Art">{ART_LABEL[p.art]}</Row>
      {p.art !== 'sofort' && <Row label="Enddatum">{endDate ? formatIsoDayDe(endDate) : 'abgeleitet (Fristregel)'}</Row>}
      {p.receivedOn && <Row label="Eingegangen">{formatIsoDayDe(p.receivedOn)}</Row>}
      {p.reason && <Row label="Grund">{p.reason}</Row>}
      {p.signerNames.length > 0 && <Row label="Unterzeichnet von">{p.signerNames.join(', ')}</Row>}
      <Row label="Erkennung">{(p.confidence * 100).toFixed(0)} %</Row>
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const t = terminationErrorText(error);
  return (
    <p className="text-xs text-destructive" role="alert">
      {t.message}
      {t.hint ? ` ${t.hint}` : ''}
    </p>
  );
}

function useProposal(taskId: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.terminationProposal(taskId),
    queryFn: () => apiClient.mandateTermination.proposal(taskId),
    enabled,
    retry: false,
  });
}

/** «Mögliche Kündigung prüfen»: apply (the dialog, prefilled) or discard. */
function ProposalReview({ taskId, active }: { taskId: string; active: boolean }) {
  const qc = useQueryClient();
  const { can } = useEffectivePermissions();
  const proposal = useProposal(taskId, true);
  const [dialog, setDialog] = useState(false);
  const discard = useMutation({
    mutationFn: () => apiClient.mandateTermination.discardProposal(taskId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.terminationProposal(taskId) });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
  if (proposal.isLoading) return <p className="text-sm text-muted-foreground">Lädt …</p>;
  if (proposal.isError) {
    return isNotFound(proposal.error) ? (
      <p className="text-sm text-muted-foreground">Zu dieser Aufgabe liegt keine erkannte Kündigung vor.</p>
    ) : (
      <ErrorLine error={proposal.error} />
    );
  }
  const p = proposal.data!;
  const open = p.decision === null && active;
  const subjectType = p.subjectType as SubjectType;
  return (
    <div className="flex flex-col gap-2 text-sm">
      <ProposalFacts p={p} />
      {p.decision !== null && <p className="text-xs text-muted-foreground">{DECISION_LABEL[p.decision]}</p>}
      {open && can(TERMINATION_INTAKE_KEY) && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={discard.isPending} onClick={() => setDialog(true)}>
            Mandat auflösen
          </Button>
          <Button size="sm" variant="outline" disabled={discard.isPending} onClick={() => discard.mutate()}>
            Verwerfen
          </Button>
        </div>
      )}
      {discard.isError && <ErrorLine error={discard.error} />}
      {dialog && (
        <MandatAufloesenDialog
          subjectType={subjectType}
          subjectId={p.subjectId}
          proposal={p}
          onClose={() => setDialog(false)}
          onDone={() => {
            setDialog(false);
            void qc.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}
    </div>
  );
}

/** «Kündigung prüfen»: the Befunde in words, «Berechtigung bestätigt» or «Zurückweisen». */
function CheckReview({ subjectType, subjectId, active }: { subjectType: SubjectType; subjectId: string; active: boolean }) {
  const qc = useQueryClient();
  const { can } = useEffectivePermissions();
  const live = useQuery({
    queryKey: qk.termination(subjectType, subjectId),
    queryFn: () => apiClient.mandateTermination.live(subjectType, subjectId),
  });
  const [reasonCode, setReasonCode] = useState<TerminationOverrideReasonCode | ''>('');
  const [remark, setRemark] = useState('');
  const settled = () => {
    void qc.invalidateQueries({ queryKey: qk.termination(subjectType, subjectId) });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    refreshMandateQueries(qc, subjectId, subjectType);
  };
  const confirm = useMutation({
    mutationFn: (id: string) => apiClient.mandateTermination.confirmAuthorization(id, reasonCode as TerminationOverrideReasonCode),
    onSuccess: settled,
  });
  const reject = useMutation({
    mutationFn: (id: string) => apiClient.mandateTermination.reject(id, remark.trim()),
    onSuccess: settled,
  });
  if (live.isLoading) return <p className="text-sm text-muted-foreground">Lädt …</p>;
  if (live.isError) return <ErrorLine error={live.error} />;
  const t = live.data ?? null;
  if (t === null || t.status !== 'in_pruefung') {
    return <p className="text-sm text-muted-foreground">Die Berechtigungsprüfung ist entschieden.</p>;
  }
  const codes = t.checkResult?.codes ?? [];
  const busy = confirm.isPending || reject.isPending;
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p>
        {t.processNumber}
        {t.receivedOn ? ` · eingegangen ${formatIsoDayDe(t.receivedOn)}` : ''}
      </p>
      <ul className="flex flex-col gap-0.5" data-testid="termination-check-codes">
        {codes.map((c) => (
          <li key={c} className="text-status-warning">
            {TERMINATION_CHECK_CODE_LABEL[c] ?? c}
          </li>
        ))}
      </ul>
      {active && can(TERMINATION_INTAKE_KEY) && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              Grund der Bestätigung
              <select
                aria-label="Grund der Bestätigung"
                className="w-60 rounded border px-2 py-1 text-sm"
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value as TerminationOverrideReasonCode | '')}
              >
                <option value="">Bitte wählen</option>
                {TERMINATION_OVERRIDE_REASON_CODES.map((c) => (
                  <option key={c} value={c}>
                    {TERMINATION_OVERRIDE_REASON_LABEL[c]}
                  </option>
                ))}
              </select>
            </label>
            <Button size="sm" disabled={reasonCode === '' || busy} onClick={() => confirm.mutate(t.id)}>
              Berechtigung bestätigt
            </Button>
          </div>
          <div className="flex flex-col gap-1">
            <label className="flex flex-col gap-1">
              Begründung (Pflicht beim Zurückweisen)
              <textarea
                aria-label="Begründung"
                className="min-h-14 rounded border px-2 py-1 text-sm"
                maxLength={TERMINATION_REMARK_MAX}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <div>
              <Button size="sm" variant="outline" disabled={remark.trim() === '' || busy} onClick={() => reject.mutate(t.id)}>
                Zurückweisen
              </Button>
            </div>
          </div>
        </div>
      )}
      {confirm.isError && <ErrorLine error={confirm.error} />}
      {reject.isError && <ErrorLine error={reject.error} />}
    </div>
  );
}

/** A hint task: what to do, and the Kündigung read from the mail where the task carries one. */
function HintSection({ taskId, kind }: { taskId: string; kind: TerminationIntakeTaskKind }) {
  const withProposal = kind !== 'termination_zuordnen';
  const proposal = useProposal(taskId, withProposal);
  const unverified = (proposal.data?.reviewReasons ?? []).includes('absender_nicht_verifiziert');
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="text-muted-foreground">{(unverified ? HINT_TEXT_UNVERIFIED[kind] : undefined) ?? HINT_TEXT[kind]}</p>
      {withProposal && proposal.data && <ProposalFacts p={proposal.data} />}
      {withProposal && proposal.isError && !isNotFound(proposal.error) && <ErrorLine error={proposal.error} />}
    </div>
  );
}

export function TerminationTaskSection({
  taskId,
  kind,
  subjectType,
  subjectId,
  active,
}: {
  taskId: string;
  kind: TerminationIntakeTaskKind;
  subjectType: string | null | undefined;
  subjectId: string | null | undefined;
  /** The task is still open (a done or cancelled task shows the facts only). */
  active: boolean;
}) {
  if (kind === 'termination_proposal') return <ProposalReview taskId={taskId} active={active} />;
  if (kind === 'termination_check') {
    if ((subjectType !== 'person' && subjectType !== 'firma') || !subjectId) return null;
    return <CheckReview subjectType={subjectType} subjectId={subjectId} active={active} />;
  }
  return <HintSection taskId={taskId} kind={kind} />;
}

/** «Mandatsauflösung freigeben»: the dialog «Mandat auflösen», resumed on the customer's live termination. */
export function TerminationFreigabeSection({
  subjectType,
  subjectId,
  active,
}: {
  subjectType: string | null | undefined;
  subjectId: string | null | undefined;
  active: boolean;
}) {
  const qc = useQueryClient();
  const { can } = useEffectivePermissions();
  const [dialog, setDialog] = useState(false);
  if ((subjectType !== 'person' && subjectType !== 'firma') || !subjectId) return null;
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p className="text-muted-foreground">Die Mandatsauflösung ist eröffnet und wartet auf die Freigabe.</p>
      {active && !can(TERMINATION_FREIGABE_KEY) && (
        <p className="text-muted-foreground">Die Freigabe braucht die Berechtigung zur Mandatsfreigabe.</p>
      )}
      {active && can(TERMINATION_FREIGABE_KEY) && (
        <div>
          <Button size="sm" onClick={() => setDialog(true)}>
            Mandat auflösen
          </Button>
        </div>
      )}
      {dialog && (
        <MandatAufloesenDialog
          subjectType={subjectType}
          subjectId={subjectId}
          onClose={() => setDialog(false)}
          onDone={() => {
            setDialog(false);
            void qc.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}
    </div>
  );
}
